import { useDraggable } from '@dnd-kit/core';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react';
import { useAppSelector } from 'app/hooks';
import store from 'app/store';
import clsx from 'clsx';
import { useContextMenu } from 'components/common/context-menu-manager';
import {
  ContextMenuItemType,
  DuctRunPointType,
  GridSpaceDataType
} from 'data/types';
import { useDuctRunActions } from 'hooks/use-duct-run-actions';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  routedSameLevelRunPreview,
  routedSupplyRunPreview,
  RoutedRunPreviewType,
  ZoneConcealedDuctLayout
} from 'utils/concealed-duct-geometry';
import { findZoneUniqueIdByZoneItemUniqueId } from 'utils/configurations';
import { midpointAlongPath, RoutePointType } from 'utils/duct-route';
import {
  resolveDuctRunEmphasis,
  scaledRunOpacity
} from 'utils/duct-run-emphasis';
import {
  buildDuctRunPathPx,
  clampReturnEndpointPx,
  clampSupplyEndpointPx,
  ductRunEndpointKey,
  DuctRunRef,
  ductRunRefKey,
  ductRunWaypointKey,
  PointPx,
  snapPxToGrid
} from 'utils/duct-runs';
import DndMonitor, { DndMonitorListenersType } from './dnd-monitor';

type GridDuctRunsProps = Readonly<{
  layouts: ZoneConcealedDuctLayout[];
  currentLevelIndex: number;
  gridSpaces: GridSpaceDataType[];
  configurationId?: string;
  zoom?: number;
  onBeforeCommit?: () => void;
  readOnly?: boolean;
}>;

const DUCT_POINT_DRAG_TYPE = 'grid-cd-point';
const AIR_HANDLER_DRAG_TYPE = 'grid-air-handler';

const SUPPLY_COLOR = '#0ea5e9'; // sky-500
const RETURN_COLOR = '#f59e0b'; // amber-500

type PointTransform = { dx: number; dy: number };

type DuctPointHandleProps = Readonly<{
  pointId: string;
  data: Record<string, unknown>;
  cx: number;
  cy: number;
  fill: string;
  radius?: number;
  tooltip?: string;
  menuItems?: ContextMenuItemType[];
  readOnly?: boolean;
  dimmed?: boolean;
}>;

function DuctPointHandle({
  pointId,
  data,
  cx,
  cy,
  fill,
  radius = 5,
  tooltip,
  menuItems,
  readOnly = false,
  dimmed = false
}: DuctPointHandleProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: pointId,
    data,
    disabled: readOnly
  });
  const { openContextMenu } = useContextMenu();

  const [hoverOpen, setHoverOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: hoverOpen,
    onOpenChange: setHoverOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 5 })]
  });
  const hover = useHover(context, { move: false });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const setCircleRef = useCallback(
    (el: SVGCircleElement | null) => {
      const ref = el as unknown as HTMLElement | null;
      setNodeRef(ref);
      refs.setReference(ref);
    },
    [setNodeRef, refs]
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<SVGCircleElement>) => {
      if (readOnly || !menuItems || menuItems.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      openContextMenu(e.clientX, e.clientY, menuItems, pointId);
    },
    [readOnly, menuItems, openContextMenu, pointId]
  );

  const showTooltip = !!tooltip && hoverOpen && !isDragging;

  return (
    <>
      <circle
        ref={setCircleRef}
        cx={cx}
        cy={cy}
        r={radius}
        fill={fill}
        stroke='#ffffff'
        strokeWidth={2}
        className={clsx(
          'pointer-events-auto',
          readOnly ? 'cursor-default' : 'cursor-grab',
          isDragging && 'cursor-grabbing opacity-80',
          dimmed && !isDragging && 'opacity-30'
        )}
        data-cd-endpoint
        {...attributes}
        {...listeners}
        {...getReferenceProps()}
        onContextMenu={handleContextMenu}
      />
      {showTooltip && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className='z-50 w-max max-w-[300px] whitespace-pre-line rounded bg-[#444] px-2 py-1 text-sm text-white'
            {...getFloatingProps()}
          >
            {tooltip}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

type RunLengthLabelProps = Readonly<{
  path: RoutePointType[];
  lengthFt: number;
}>;

function RunLengthLabel({ path, lengthFt }: RunLengthLabelProps) {
  const anchor = midpointAlongPath(path);
  return (
    <text
      x={anchor.x}
      y={anchor.y - 6}
      textAnchor='middle'
      fontSize={11}
      fontWeight={600}
      fill='#334155'
      stroke='#ffffff'
      strokeWidth={3}
      paintOrder='stroke'
      className='pointer-events-none select-none'
    >
      {`${lengthFt} ft`}
    </text>
  );
}

type DuctRunActionsType = ReturnType<typeof useDuctRunActions>;

type DuctRunProps = Readonly<{
  runRef: DuctRunRef;
  originPx: PointPx;
  waypointsPx: DuctRunPointType[];
  autoWaypoints: boolean;
  endpointPx: PointPx;
  structureSpaceId: string | null;
  routingRegionPx: RoutePointType[][];
  originDragging: boolean;
  color: string;
  strokeWidth: number;
  dashArray: string;
  lineOpacity: number;
  endpointRadius: number;
  endpointTooltip?: string;
  pinReturnEndpointPx?: PointPx;
  pointTransforms: Map<string, PointTransform>;
  actions: DuctRunActionsType;
  safeZoom: number;
  readOnly: boolean;
  dimmed?: boolean;
}>;

function DuctRun({
  runRef,
  originPx,
  waypointsPx,
  autoWaypoints,
  endpointPx,
  structureSpaceId,
  routingRegionPx,
  originDragging,
  color,
  strokeWidth,
  dashArray,
  lineOpacity,
  endpointRadius,
  endpointTooltip,
  pinReturnEndpointPx,
  pointTransforms,
  actions,
  safeZoom,
  readOnly,
  dimmed = false
}: DuctRunProps) {
  const { openContextMenu } = useContextMenu();
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(
    null
  );

  const applyTransform = useCallback(
    (point: PointPx, key: string): PointPx => {
      const transform = pointTransforms.get(key);
      if (!transform) return point;
      return { x: point.x + transform.dx, y: point.y + transform.dy };
    },
    [pointTransforms]
  );

  const endpointKey = ductRunEndpointKey(runRef);
  const displayedWaypoints = waypointsPx.map((point) => ({
    ...point,
    ...applyTransform(point, ductRunWaypointKey(runRef, point.uniqueId))
  }));
  const displayedEndpoint = applyTransform(endpointPx, endpointKey);

  const endpointDragging = pointTransforms.has(endpointKey);
  const isPrimarySupply =
    runRef.kind === 'supply' && runRef.runUniqueId === null;
  const previewFn = isPrimarySupply
    ? routedSupplyRunPreview
    : routedSameLevelRunPreview;
  const routedPreview: RoutedRunPreviewType | null =
    autoWaypoints && !readOnly && (originDragging || endpointDragging)
      ? previewFn(routingRegionPx, originPx, displayedEndpoint)
      : null;

  const path = routedPreview
    ? routedPreview.pathPx
    : buildDuctRunPathPx(originPx, displayedWaypoints, displayedEndpoint);

  const basePointsPx = autoWaypoints ? waypointsPx : undefined;

  const clickPointPx = useCallback(
    (e: React.MouseEvent<SVGLineElement>): PointPx | null => {
      const rect = (e.currentTarget as SVGLineElement)
        .closest('svg')
        ?.getBoundingClientRect();
      if (!rect) return null;
      return {
        x: (e.clientX - rect.left) / safeZoom,
        y: (e.clientY - rect.top) / safeZoom
      };
    },
    [safeZoom]
  );

  const handleAddPoint = useCallback(
    (segmentIndex: number, px: PointPx) => {
      actions.addWaypoint(
        runRef,
        segmentIndex,
        px,
        pinReturnEndpointPx,
        basePointsPx
      );
    },
    [actions, runRef, pinReturnEndpointPx, basePointsPx]
  );

  const removeRunMenuItem = useCallback(
    (): ContextMenuItemType[] => [
      {
        label:
          runRef.kind === 'supply' ? 'Remove Supply Run' : 'Remove Return Run',
        disabled: runRef.runUniqueId === null,
        onClick: () => actions.removeRun(runRef)
      }
    ],
    [actions, runRef]
  );

  const handleSegmentClick = useCallback(
    (segmentIndex: number, e: React.MouseEvent<SVGLineElement>) => {
      const px = clickPointPx(e);
      if (!px) return;
      e.stopPropagation();
      handleAddPoint(segmentIndex, px);
    },
    [clickPointPx, handleAddPoint]
  );

  const handleSegmentContextMenu = useCallback(
    (segmentIndex: number, e: React.MouseEvent<SVGLineElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const px = clickPointPx(e);
      const menuItems: ContextMenuItemType[] = [];
      if (px) {
        menuItems.push({
          label: 'Add Point',
          onClick: () => handleAddPoint(segmentIndex, px)
        });
      }
      menuItems.push(...removeRunMenuItem());
      if (menuItems.length === 0) return;
      openContextMenu(
        e.clientX,
        e.clientY,
        menuItems,
        `${ductRunRefKey(runRef)}::segment::${segmentIndex}`
      );
    },
    [clickPointPx, handleAddPoint, removeRunMenuItem, openContextMenu, runRef]
  );

  const waypointMenuItems = useCallback(
    (pointUniqueId: string): ContextMenuItemType[] => [
      {
        label: 'Delete Point',
        onClick: () =>
          actions.deleteWaypoint(
            runRef,
            pointUniqueId,
            basePointsPx,
            pinReturnEndpointPx
          )
      },
      ...removeRunMenuItem()
    ],
    [actions, runRef, basePointsPx, pinReturnEndpointPx, removeRunMenuItem]
  );

  return (
    <g>
      {path.slice(0, -1).map((point, segmentIndex) => {
        const nextPoint = path[segmentIndex + 1];
        const segmentId = `${ductRunRefKey(runRef)}::segment::${segmentIndex}`;
        const isHovered = hoveredSegmentIndex === segmentIndex;
        return (
          <g key={segmentId}>
            <line
              x1={point.x}
              y1={point.y}
              x2={nextPoint.x}
              y2={nextPoint.y}
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={dashArray}
              strokeLinecap='round'
              opacity={lineOpacity}
            />
            {isHovered && !readOnly && !routedPreview && (
              <line
                x1={point.x}
                y1={point.y}
                x2={nextPoint.x}
                y2={nextPoint.y}
                stroke={color}
                strokeWidth={strokeWidth * 4}
                strokeLinecap='round'
                className='pointer-events-none opacity-30'
              />
            )}
            {!readOnly && !routedPreview && (
              <line
                x1={point.x}
                y1={point.y}
                x2={nextPoint.x}
                y2={nextPoint.y}
                stroke='transparent'
                strokeWidth={10}
                className='pointer-events-auto cursor-pointer'
                data-cd-endpoint
                onMouseEnter={() => setHoveredSegmentIndex(segmentIndex)}
                onMouseLeave={() =>
                  setHoveredSegmentIndex((prev) =>
                    prev === segmentIndex ? null : prev
                  )
                }
                onClick={(e) => handleSegmentClick(segmentIndex, e)}
                onContextMenu={(e) => handleSegmentContextMenu(segmentIndex, e)}
              />
            )}
          </g>
        );
      })}
      {routedPreview && (
        <RunLengthLabel
          path={routedPreview.pathPx}
          lengthFt={routedPreview.lengthFt}
        />
      )}
      {!routedPreview &&
        waypointsPx.map((point, index) => (
          <DuctPointHandle
            key={point.uniqueId}
            pointId={ductRunWaypointKey(runRef, point.uniqueId)}
            data={{
              type: DUCT_POINT_DRAG_TYPE,
              runRef,
              pointUniqueId: point.uniqueId,
              startPx: { x: point.x, y: point.y },
              basePointsPx,
              pinReturnEndpointPx
            }}
            cx={displayedWaypoints[index].x}
            cy={displayedWaypoints[index].y}
            fill={color}
            radius={4}
            menuItems={waypointMenuItems(point.uniqueId)}
            readOnly={readOnly}
            dimmed={dimmed}
          />
        ))}
      <DuctPointHandle
        pointId={endpointKey}
        data={{
          type: DUCT_POINT_DRAG_TYPE,
          runRef,
          pointUniqueId: null,
          structureSpaceId,
          startPx: { x: endpointPx.x, y: endpointPx.y }
        }}
        cx={displayedEndpoint.x}
        cy={displayedEndpoint.y}
        fill={color}
        radius={endpointRadius}
        tooltip={endpointTooltip}
        menuItems={removeRunMenuItem()}
        readOnly={readOnly}
        dimmed={dimmed}
      />
    </g>
  );
}

export default function GridDuctRuns({
  layouts,
  currentLevelIndex,
  gridSpaces,
  configurationId,
  zoom = 1,
  onBeforeCommit,
  readOnly = false
}: GridDuctRunsProps) {
  const safeZoom = zoom > 0 ? zoom : 1;
  const actions = useDuctRunActions(configurationId, onBeforeCommit);
  const hoveredConcealedZoneId = useAppSelector(
    (state) => state.ui.hoveredConcealedZoneId
  );

  const visible = layouts.filter(
    (l) => l.originLevelIndex === currentLevelIndex
  );
  const visibleRef = useRef(visible);
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  const [pointTransforms, setPointTransforms] = useState<
    Map<string, PointTransform>
  >(new Map());
  const pointTransformsRef = useRef(pointTransforms);
  useEffect(() => {
    pointTransformsRef.current = pointTransforms;
  }, [pointTransforms]);

  const [originDrag, setOriginDrag] = useState<{
    zoneUniqueId: string;
    dx: number;
    dy: number;
  } | null>(null);
  const airHandlerDragZoneRef = useRef<string | null>(null);

  const rawEmphasisZoneId = originDrag?.zoneUniqueId ?? hoveredConcealedZoneId;
  const emphasisZoneId = visible.some(
    (l) => l.zoneUniqueId === rawEmphasisZoneId
  )
    ? rawEmphasisZoneId
    : null;
  const orderedVisible = emphasisZoneId
    ? [...visible].sort(
        (a, b) =>
          Number(a.zoneUniqueId === emphasisZoneId) -
          Number(b.zoneUniqueId === emphasisZoneId)
      )
    : visible;

  const resolveAirHandlerZoneUniqueId = useCallback(
    (activeId: string) => {
      if (!configurationId) return null;
      const config = store
        .getState()
        .configurations.configurations.find(
          (c) => c.uniqueId === configurationId
        );
      return findZoneUniqueIdByZoneItemUniqueId(
        config,
        activeId.replace('-air-handler', '')
      );
    },
    [configurationId]
  );

  const findRoomGridSpace = useCallback(
    (structureSpaceId: string | null) =>
      gridSpaces.find((gs) => gs.spaceId === structureSpaceId),
    [gridSpaces]
  );

  const clearTransform = useCallback((id: string) => {
    setPointTransforms((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const dragListeners: DndMonitorListenersType = {
    onDragStart: (event) => {
      const t = event.active.data.current?.type;
      if (t === DUCT_POINT_DRAG_TYPE) {
        document.body.style.cursor = 'grabbing';
      }
      if (t === AIR_HANDLER_DRAG_TYPE) {
        airHandlerDragZoneRef.current = resolveAirHandlerZoneUniqueId(
          String(event.active.id)
        );
      }
    },
    onDragMove: (event) => {
      const { active } = event;
      const data = active.data.current;
      if (data?.type === AIR_HANDLER_DRAG_TYPE) {
        const zoneUniqueId = airHandlerDragZoneRef.current;
        const initialRect = active.rect.current.initial;
        const translatedRect = active.rect.current.translated;
        if (!zoneUniqueId || !initialRect || !translatedRect) return;
        setOriginDrag({
          zoneUniqueId,
          dx: (translatedRect.left - initialRect.left) / safeZoom,
          dy: (translatedRect.top - initialRect.top) / safeZoom
        });
        return;
      }
      if (data?.type !== DUCT_POINT_DRAG_TYPE) return;

      const initialRect = active.rect.current.initial;
      const translatedRect = active.rect.current.translated;
      if (!initialRect || !translatedRect) return;

      const dx = (translatedRect.left - initialRect.left) / safeZoom;
      const dy = (translatedRect.top - initialRect.top) / safeZoom;
      const id = String(active.id);
      const startPx = data.startPx as PointPx;

      const snapped = snapPxToGrid({
        x: startPx.x + dx,
        y: startPx.y + dy
      });
      const transform: PointTransform = {
        dx: snapped.x - startPx.x,
        dy: snapped.y - startPx.y
      };

      setPointTransforms((prev) => {
        const next = new Map(prev);
        next.set(id, transform);
        return next;
      });
    },
    onDragEnd: (event) => {
      const { active } = event;
      const data = active.data.current;
      if (data?.type === AIR_HANDLER_DRAG_TYPE) {
        airHandlerDragZoneRef.current = null;
        setOriginDrag(null);
        return;
      }
      if (data?.type !== DUCT_POINT_DRAG_TYPE) return;
      document.body.style.cursor = '';

      const id = String(active.id);
      const transform = pointTransformsRef.current.get(id);
      clearTransform(id);
      if (!transform) return;

      const runRef = data.runRef as DuctRunRef;
      const startPx = data.startPx as PointPx;
      const proposed = {
        x: startPx.x + transform.dx,
        y: startPx.y + transform.dy
      };

      const pointUniqueId = data.pointUniqueId as string | null;
      if (pointUniqueId) {
        actions.moveWaypoint(
          runRef,
          pointUniqueId,
          proposed,
          data.basePointsPx as DuctRunPointType[] | undefined,
          data.pinReturnEndpointPx as PointPx | undefined
        );
        return;
      }

      if (runRef.kind === 'supply') {
        const structureSpaceId =
          (data.structureSpaceId as string | null) ?? null;
        const room = findRoomGridSpace(structureSpaceId);
        if (!room) return;
        const clamped = clampSupplyEndpointPx(
          proposed,
          structureSpaceId,
          gridSpaces
        );
        if (clamped) {
          actions.moveEndpoint(runRef, clamped);
        }
        return;
      }

      const layout = visibleRef.current.find(
        (l) => l.zoneUniqueId === runRef.zoneUniqueId
      );
      if (!layout) return;
      actions.moveEndpoint(
        runRef,
        clampReturnEndpointPx(proposed, layout, gridSpaces)
      );
    },
    onDragCancel: (event) => {
      const { active } = event;
      const t = active.data.current?.type;
      if (t === AIR_HANDLER_DRAG_TYPE) {
        airHandlerDragZoneRef.current = null;
        setOriginDrag(null);
        return;
      }
      if (t !== DUCT_POINT_DRAG_TYPE) return;
      document.body.style.cursor = '';
      clearTransform(String(active.id));
    }
  };

  if (visible.length === 0) return null;

  return (
    <>
      {!readOnly && <DndMonitor listeners={dragListeners} />}
      <svg
        className='pointer-events-none absolute inset-0 z-[19] h-full w-full overflow-visible'
        style={{ overflow: 'visible' }}
      >
        {orderedVisible.map((layout) => {
          if (!layout.zoneUniqueId) return null;
          const { zoneUniqueId, condenserUniqueId } = layout;
          const emphasis = resolveDuctRunEmphasis(emphasisZoneId, zoneUniqueId);
          const supplyStrokeWidth = 2 * emphasis.strokeWidthScale;
          const returnStrokeWidth = 2.5 * emphasis.strokeWidthScale;
          const supplyOpacity = scaledRunOpacity(0.7, emphasis);
          const returnOpacity = scaledRunOpacity(0.85, emphasis);

          const passiveSpaceIds = new Set(
            layout.branches
              .filter((branch) => branch.servedPassively)
              .map((branch) => branch.spaceUniqueId)
          );

          const ret = layout.returnRun;

          const activeOriginDrag =
            originDrag?.zoneUniqueId === zoneUniqueId ? originDrag : null;
          const originPoint = {
            x: layout.originPx.x + (activeOriginDrag?.dx ?? 0),
            y: layout.originPx.y + (activeOriginDrag?.dy ?? 0)
          };
          const routingRegion = layout.routingRegionPx ?? [];

          return (
            <g key={`duct-runs-${zoneUniqueId}`}>
              {/* Primary supply runs - one per zoneItem. Never removable. */}
              {layout.branches.map((branch) => {
                if (
                  !branch.sameLevelAsOrigin ||
                  !branch.centroidPx ||
                  branch.servedPassively
                ) {
                  return null;
                }

                const room = findRoomGridSpace(branch.structureSpaceId);
                const roomLabel = room?.label;
                const supplyTooltip = roomLabel
                  ? `Supply \u2192 ${roomLabel}\n${branch.ductLengthFt} ft`
                  : `Supply: ${branch.ductLengthFt} ft`;

                const runRef: DuctRunRef = {
                  zoneUniqueId,
                  condenserUniqueId,
                  kind: 'supply',
                  spaceUniqueId: branch.spaceUniqueId,
                  runUniqueId: null
                };

                return (
                  <DuctRun
                    key={`supply-primary-${branch.spaceUniqueId}`}
                    runRef={runRef}
                    originPx={originPoint}
                    waypointsPx={branch.waypointsPx}
                    autoWaypoints={!!branch.autoWaypoints}
                    endpointPx={branch.centroidPx}
                    structureSpaceId={branch.structureSpaceId}
                    routingRegionPx={routingRegion}
                    originDragging={!!activeOriginDrag}
                    color={SUPPLY_COLOR}
                    strokeWidth={supplyStrokeWidth}
                    dashArray='6 4'
                    lineOpacity={supplyOpacity}
                    endpointRadius={5}
                    endpointTooltip={supplyTooltip}
                    pointTransforms={pointTransforms}
                    actions={actions}
                    safeZoom={safeZoom}
                    readOnly={readOnly}
                    dimmed={emphasis.dimmed}
                  />
                );
              })}
              {/* Additional supply runs - 0+ per room. Removable. */}
              {layout.additionalSupplyRuns.map((run) => {
                if (
                  !run.sameLevelAsOrigin ||
                  passiveSpaceIds.has(run.spaceUniqueId)
                ) {
                  return null;
                }

                const room = findRoomGridSpace(run.structureSpaceId);
                const roomLabel = room?.label;
                const tooltip = roomLabel
                  ? `Supply \u2192 ${roomLabel}\n${run.ductLengthFt} ft`
                  : `Supply: ${run.ductLengthFt} ft`;

                const runRef: DuctRunRef = {
                  zoneUniqueId,
                  condenserUniqueId,
                  kind: 'supply',
                  spaceUniqueId: run.spaceUniqueId,
                  runUniqueId: run.runUniqueId
                };

                return (
                  <DuctRun
                    key={`supply-additional-${run.runUniqueId}`}
                    runRef={runRef}
                    originPx={originPoint}
                    waypointsPx={run.waypointsPx}
                    autoWaypoints={!!run.autoWaypoints}
                    endpointPx={run.endpointPx}
                    structureSpaceId={run.structureSpaceId}
                    routingRegionPx={routingRegion}
                    originDragging={!!activeOriginDrag}
                    color={SUPPLY_COLOR}
                    strokeWidth={supplyStrokeWidth}
                    dashArray='6 4'
                    lineOpacity={supplyOpacity}
                    endpointRadius={5}
                    endpointTooltip={tooltip}
                    pointTransforms={pointTransforms}
                    actions={actions}
                    safeZoom={safeZoom}
                    readOnly={readOnly}
                    dimmed={emphasis.dimmed}
                  />
                );
              })}
              {/* Primary return run - exactly one per zone, never removable. */}
              {ret && ret.sameLevelAsOrigin && (
                <DuctRun
                  runRef={{
                    zoneUniqueId,
                    condenserUniqueId,
                    kind: 'return',
                    runUniqueId: null
                  }}
                  originPx={originPoint}
                  waypointsPx={ret.waypointsPx}
                  autoWaypoints={!!ret.autoWaypoints}
                  endpointPx={ret.endpointPx}
                  structureSpaceId={ret.containingSpaceId}
                  routingRegionPx={routingRegion}
                  originDragging={!!activeOriginDrag}
                  color={RETURN_COLOR}
                  strokeWidth={returnStrokeWidth}
                  dashArray='2 5'
                  lineOpacity={returnOpacity}
                  endpointRadius={6}
                  endpointTooltip={(() => {
                    const room = findRoomGridSpace(ret.containingSpaceId);
                    const label = room?.label;
                    return label
                      ? `Return \u2192 ${label}\n${ret.ductLengthFt} ft`
                      : `Return: ${ret.ductLengthFt} ft`;
                  })()}
                  pinReturnEndpointPx={ret.endpointPx}
                  pointTransforms={pointTransforms}
                  actions={actions}
                  safeZoom={safeZoom}
                  readOnly={readOnly}
                  dimmed={emphasis.dimmed}
                />
              )}
              {/* Additional return runs - 0+ per zone, removable. */}
              {layout.additionalReturnRuns.map((run) => {
                if (!run.sameLevelAsOrigin) return null;

                const room = findRoomGridSpace(run.containingSpaceId);
                const tooltip = room?.label
                  ? `Return \u2192 ${room.label}\n${run.ductLengthFt} ft`
                  : `Return: ${run.ductLengthFt} ft`;

                return (
                  <DuctRun
                    key={`return-additional-${run.runUniqueId}`}
                    runRef={{
                      zoneUniqueId,
                      condenserUniqueId,
                      kind: 'return',
                      runUniqueId: run.runUniqueId
                    }}
                    originPx={originPoint}
                    waypointsPx={run.waypointsPx}
                    autoWaypoints={!!run.autoWaypoints}
                    endpointPx={run.endpointPx}
                    structureSpaceId={run.containingSpaceId}
                    routingRegionPx={routingRegion}
                    originDragging={!!activeOriginDrag}
                    color={RETURN_COLOR}
                    strokeWidth={returnStrokeWidth}
                    dashArray='2 5'
                    lineOpacity={returnOpacity}
                    endpointRadius={6}
                    endpointTooltip={tooltip}
                    pointTransforms={pointTransforms}
                    actions={actions}
                    safeZoom={safeZoom}
                    readOnly={readOnly}
                    dimmed={emphasis.dimmed}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
    </>
  );
}
